import { useEffect, useRef, useState, type RefObject } from 'react';

/* Motion, ported from duelapi.com's own js/dock.js and js/motion.js (same
   owner). Everything is tied to scroll position or runs once on arrival:
   nothing loops, and nothing moves while it is being read. */

const finite = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);
const ease = (t: number) => 1 - Math.pow(1 - t, 3);

/** The header slims, rounds and darkens over the first 110px of scroll. The CSS
    turns the --p custom property (0 at rest, 1 condensed) into geometry. */
export function useHeaderCondense(ref: RefObject<HTMLElement | null>) {
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    // Scroll events already arrive at most once per frame, so this writes the
    // value directly; routing it through requestAnimationFrame added nothing
    // and froze the header in any tab the browser had stopped painting.
    const run = () => {
      const p = ease(Math.min(1, Math.max(0, window.scrollY / 110)));
      el.style.setProperty('--p', p.toFixed(3));
      el.classList.toggle('stuck', p > 0);
    };
    window.addEventListener('scroll', run, { passive: true });
    window.addEventListener('resize', run);
    run();
    return () => { window.removeEventListener('scroll', run); window.removeEventListener('resize', run); };
  }, [ref]);
}

/** Tiles below the first screen rise in as they arrive, staggered when several
    land together. The dashboard grid has its own entrance and is left alone.
    A safety timer reveals anything the observer never reported, so a tile can
    never be stuck invisible. */
export function useReveal(key: string) {
  useEffect(() => {
    if (!('IntersectionObserver' in window)) return;
    const els = [...document.querySelectorAll<HTMLElement>('#main .tile, #main .report-card, #main .story-stats li')]
      .filter((el) => !el.closest('.dash') && !el.closest('.report-card') && !el.classList.contains('story-body'));
    els.forEach((el) => el.classList.add('reveal'));
    const settle = (el: HTMLElement, delay: number) => {
      el.style.setProperty('--delay', `${delay}ms`);
      el.classList.add('in');
      window.setTimeout(() => { el.classList.remove('reveal', 'in'); el.style.removeProperty('--delay'); }, delay + 1600);
    };
    const io = new IntersectionObserver((entries) => {
      entries.filter((e) => e.isIntersecting).forEach((entry, k) => {
        io.unobserve(entry.target);
        settle(entry.target as HTMLElement, Math.min(k, 4) * 80);
      });
    }, { rootMargin: '0px 0px -8% 0px' });
    els.forEach((el) => io.observe(el));
    const safety = window.setTimeout(() => {
      els.filter((el) => el.classList.contains('reveal') && !el.classList.contains('in')).forEach((el) => settle(el, 0));
    }, 2500);
    return () => { io.disconnect(); window.clearTimeout(safety); };
  }, [key]);
}

/** A number that counts from its last value to its new one, the way duelapi's
    countUp() does. The final value is always what is rendered at the end. */
export function Count({ value, format }: { value: number | null | undefined; format: (v: number | null | undefined) => string }) {
  const [shown, setShown] = useState<number | null>(null);
  const last = useRef(0);
  useEffect(() => {
    if (!finite(value)) { setShown(null); return; }
    const from = last.current;
    last.current = value;
    const start = performance.now();
    let raf = 0;
    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / 900);
      setShown(from + (value - from) * ease(t));
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [value]);
  return <>{format(finite(value) ? (shown ?? 0) : value)}</>;
}
